import { callable } from '../callable';

export type AttendanceStatus = 'present' | 'absent' | 'late' | 'excused';
export type AttendanceSource = 'teacher' | 'self' | 'zoom';
export type Modality = 'in-person' | 'online' | 'hybrid';

export interface SessionRosterEntryDto {
  studentId: string;
  name: string;
  status: AttendanceStatus | 'none';
  source?: AttendanceSource;
}

export interface DayAttendanceDto {
  moduleId: string;
  sectionId: string;
  date: string;
  modality: Modality;
  topics: string[];
  attendanceTaken: boolean;
  roster: SessionRosterEntryDto[];
}

export interface StudentDayDto {
  moduleId: string;
  date: string;
  startTime: string;
  endTime: string;
  topics: string[];
  modality: Modality;
  status: AttendanceStatus | 'none';
  canSelfMark: boolean;
}

export interface StudentDayModuleDto {
  moduleId: string;
  title: string;
  sortOrder: number;
  days: StudentDayDto[];
}

export interface AttendanceSummaryRowDto {
  studentId: string;
  name: string;
  present: number;
  absent: number;
  late: number;
  excused: number;
  markedDays: number;
  pct: number | null;
  atRisk: boolean;
}

export interface AttendanceMatrixDto {
  days: { moduleId: string; date: string }[];
  students: { studentId: string; name: string; statuses: (AttendanceStatus | 'none')[] }[];
}

export const attendanceApi = {
  // ── Teacher ──────────────────────────────────────────────────────────────
  updateDayModality: async (data: {
    tenantId: string;
    moduleId: string;
    date: string;
    modality: Modality;
  }): Promise<{ success: boolean }> => {
    const fn = callable<typeof data, { success: boolean }>('setDayModality');
    return (await fn(data)).data;
  },

  getDayAttendance: async (data: {
    tenantId: string;
    moduleId: string;
    date: string;
  }): Promise<DayAttendanceDto> => {
    const fn = callable<typeof data, DayAttendanceDto>('getSessionAttendance');
    return (await fn(data)).data;
  },

  markDayAttendance: async (data: {
    tenantId: string;
    moduleId: string;
    date: string;
    marks: { studentId: string; status: AttendanceStatus; note?: string }[];
  }): Promise<{ marked: number }> => {
    const fn = callable<typeof data, { marked: number }>('markSessionAttendance');
    return (await fn(data)).data;
  },

  getSectionAttendanceSummary: async (data: {
    tenantId: string;
    sectionId: string;
  }): Promise<{ minPct: number; rows: AttendanceSummaryRowDto[] }> => {
    const fn = callable<typeof data, { minPct: number; rows: AttendanceSummaryRowDto[] }>(
      'getSectionAttendanceSummary',
    );
    return (await fn(data)).data;
  },

  getSectionAttendanceMatrix: async (data: {
    tenantId: string;
    sectionId: string;
  }): Promise<AttendanceMatrixDto> => {
    const fn = callable<typeof data, AttendanceMatrixDto>('getSectionAttendanceMatrix');
    return (await fn(data)).data;
  },

  // ── Student ──────────────────────────────────────────────────────────────
  getStudentCourseDays: async (data: {
    tenantId: string;
    studentId: string;
    sectionId: string;
  }): Promise<{ minPct: number; modules: StudentDayModuleDto[] }> => {
    const fn = callable<typeof data, { minPct: number; modules: StudentDayModuleDto[] }>(
      'getStudentCourseSessions',
    );
    return (await fn(data)).data;
  },

  /** El alumno marca SU asistencia: el servidor lo saca de la sesión. */
  selfMarkDayAttendance: async (data: {
    tenantId: string;
    moduleId: string;
    date: string;
    status: 'present' | 'absent';
  }): Promise<{ success: boolean }> => {
    const fn = callable<typeof data, { success: boolean }>('selfMarkAttendance');
    return (await fn(data)).data;
  },
};
